import type { DriverStatus, RideStatus, SosStatus, VehicleType } from './types';

const IST = 'Asia/Kolkata';

const rupeeFmt = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 0 });
const countFmt = new Intl.NumberFormat('en-IN');
const paiseFmt = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** ₹1,23,456 — paise in, rupees out (paise shown only when not a whole rupee). */
export function rupees(paise: number | null | undefined): string {
  if (paise == null) return '—';
  const r = paise / 100;
  // whole rupees as ₹335; anything with paise always shows two places (₹33.50, never ₹33.5)
  const s = Number.isInteger(r) ? rupeeFmt.format(Math.abs(r)) : paiseFmt.format(Math.abs(r));
  return `${r < 0 ? '−' : ''}₹${s}`;
}

/** ₹1.50 style for per-unit prices (always two decimals when not whole). */
export function rupeesExact(paise: number): string {
  const r = paise / 100;
  return Number.isInteger(r) ? `₹${countFmt.format(r)}` : `₹${r.toFixed(2)}`;
}

export const count = (n: number | null | undefined) => (n == null ? '—' : countFmt.format(n));

const timeFmt = new Intl.DateTimeFormat('en-IN', { timeZone: IST, hour: 'numeric', minute: '2-digit', hour12: true });
const dayFmt = new Intl.DateTimeFormat('en-IN', { timeZone: IST, day: 'numeric', month: 'short' });
const dayYearFmt = new Intl.DateTimeFormat('en-IN', { timeZone: IST, day: 'numeric', month: 'short', year: 'numeric' });
const ymdFmt = new Intl.DateTimeFormat('en-CA', { timeZone: IST, year: 'numeric', month: '2-digit', day: '2-digit' });

/** "3:48 pm" in India time. */
export function time(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  return timeFmt.format(new Date(iso)).replace(/\s?(AM|PM)$/i, (m) => ` ${m.trim().toLowerCase()}`);
}

/** "3:48 pm" today, "19 Oct, 3:48 pm" this year, "19 Oct 2025, 3:48 pm" otherwise. */
export function dateTime(iso: string | Date | null | undefined, now = new Date()): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (ymdFmt.format(d) === ymdFmt.format(now)) return time(d);
  const sameYear = ymdFmt.format(d).slice(0, 4) === ymdFmt.format(now).slice(0, 4);
  return `${(sameYear ? dayFmt : dayYearFmt).format(d)}, ${time(d)}`;
}

const longDayFmt = new Intl.DateTimeFormat('en-IN', { timeZone: IST, weekday: 'long', day: 'numeric', month: 'long' });

/** "Tuesday, 20 October" */
export const longDay = (d: Date = new Date()) => longDayFmt.format(d);

export function day(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  return dayYearFmt.format(new Date(iso));
}

/** "just now", "4 min ago", "2 h ago". */
export function ago(iso: string | Date | null | undefined, now = Date.now()): string {
  if (!iso) return '—';
  const s = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (s < 20) return 'just now';
  if (s < 60) return `${s} s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ago`;
  return `${Math.floor(h / 24)} d ago`;
}

export function km(meters: number | null | undefined): string {
  if (meters == null) return '—';
  return meters < 1000 ? `${meters} m` : `${(meters / 1000).toFixed(1)} km`;
}

export function minutes(seconds: number | null | undefined): string {
  if (seconds == null) return '—';
  const m = Math.round(seconds / 60);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`;
}

/**
 * A phone number with its country code, as the API stores it (E.164). India reads "+91 90000 00001"; any other
 * country is shown as stored ("+447700900123"), since grouping differs by country. An old 10-digit Indian number
 * without "+" gets +91.
 */
export function phone(p: string | null | undefined): string {
  if (!p) return '—';
  const raw = p.trim();
  const digits = raw.replace(/\D/g, '');
  if (!raw.startsWith('+') && digits.length === 10) return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
  if (digits.startsWith('91') && digits.length === 12) return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
  return digits ? `+${digits}` : raw;
}

export const VEHICLE_LABEL: Record<VehicleType, string> = { AUTO: 'Auto', BIKE: 'Bike', CAR: 'Car' };

export const RIDE_STATUS_LABEL: Record<RideStatus, string> = {
  REQUESTED: 'Looking for driver',
  OFFERING: 'Offered to drivers',
  ACCEPTED: 'Driver on the way',
  ARRIVED: 'Driver at pickup',
  STARTED: 'On trip',
  COMPLETED: 'Completed',
  CANCELLED_BY_RIDER: 'Cancelled by rider',
  CANCELLED_BY_DRIVER: 'Cancelled by driver',
  CANCELLED_BY_ADMIN: 'Cancelled by staff',
  EXPIRED: 'No driver found',
};

export type Tone = 'neutral' | 'accent' | 'good' | 'warn' | 'bad';

export const RIDE_STATUS_TONE: Record<RideStatus, Tone> = {
  REQUESTED: 'warn',
  OFFERING: 'warn',
  ACCEPTED: 'accent',
  ARRIVED: 'accent',
  STARTED: 'accent',
  COMPLETED: 'good',
  CANCELLED_BY_RIDER: 'neutral',
  CANCELLED_BY_DRIVER: 'neutral',
  CANCELLED_BY_ADMIN: 'neutral',
  EXPIRED: 'bad',
};

export const DRIVER_STATUS_LABEL: Record<DriverStatus, string> = {
  PENDING: 'Waiting for review',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  SUSPENDED: 'Suspended',
};
export const DRIVER_STATUS_TONE: Record<DriverStatus, Tone> = {
  PENDING: 'warn',
  APPROVED: 'good',
  REJECTED: 'bad',
  SUSPENDED: 'bad',
};

export const SOS_STATUS_LABEL: Record<SosStatus, string> = {
  OPEN: 'Open',
  ACKNOWLEDGED: 'Someone is on it',
  RESOLVED: 'Resolved',
};
export const SOS_STATUS_TONE: Record<SosStatus, Tone> = { OPEN: 'bad', ACKNOWLEDGED: 'warn', RESOLVED: 'good' };

export const DOCUMENT_LABEL: Record<string, string> = {
  NAME: 'Name',
  VEHICLE: 'Vehicle details',
  DRIVING_LICENCE: 'Driving licence',
  VEHICLE_RC: 'Vehicle RC',
  AADHAAR: 'Aadhaar',
};

export const shortId = (id: string | null | undefined) => (id ? id.slice(-6).toUpperCase() : '—');

/** "RS" for Ravi Sharma; "?" when there's no name yet. */
export function initials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

/** "12%" / "7.5%" */
export const percent = (n: number | null | undefined) =>
  n == null ? '—' : `${Number.isInteger(n) ? n : Number(n.toFixed(2))}%`;

export const REWARD_KIND_LABEL: Record<string, string> = {
  DRIVER_CASH_BONUS: 'Cash bonus',
  RIDER_RIDE_CREDIT: 'Ride credit',
  RIDER_DISCOUNT_PERCENT: 'Percent off a ride',
};
export const REWARD_STATUS_LABEL: Record<string, string> = {
  AVAILABLE: 'Not used yet',
  APPLIED: 'On a booked ride',
  USED: 'Used',
  CREDITED: 'Paid to driver',
  VOID: 'Withdrawn',
};
export const REWARD_STATUS_TONE: Record<string, Tone> = {
  AVAILABLE: 'accent',
  APPLIED: 'warn',
  USED: 'good',
  CREDITED: 'good',
  VOID: 'neutral',
};

export const PAYMENT_STATUS_LABEL: Record<string, string> = {
  PENDING: 'Waiting for payment',
  PAID: 'Paid',
  FAILED: 'Payment failed',
  REFUNDED: 'Refunded',
  PARTIALLY_REFUNDED: 'Part refunded',
  NOT_REQUIRED: 'Nothing to pay',
};
